import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, BulkProcessingStatus, DocumentClassification, CrewMember } from '../types';
import { documentService } from '../services/documentService';
import { crewService } from '../services/crewService';

/**
 * Document Upload Page — drag-and-drop file upload with validation, progress tracking,
 * document type override, auto-classification, crew linking, and bulk processing status.
 *
 * Requirements:
 *  4.2 — Accept individual Document uploads in PDF, JPG, and PNG formats
 *  4.3 — When a user uploads a ZIP file, extract and process all contained documents
 *  5.5 — If Document identification fails, prompt the user to manually specify Document_Type
 *  13.1 — Link crew documents to crew members
 *  13.2 — Provide progress updates during bulk document processing
 *  14.1 — Auto-identify document type from content
 *  14.2 — Display suggested type with confidence
 *  14.3 — Allow user to confirm or override suggestion
 *  14.4 — Auto-refresh compliance dashboard after upload
 */

interface DocumentUploadPageProps {
  onUploadSuccess?: () => void;
}

/** Accepted MIME types and extensions. */
const ACCEPTED_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

const ACCEPTED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.zip'];

/** Size limits in bytes. */
const MAX_INDIVIDUAL_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ZIP_SIZE = 50 * 1024 * 1024; // 50MB

/** Common document types for manual identification (Req 5.5). */
const DOCUMENT_TYPES = [
  'First Aid Certificate',
  'Sea Safety Certificate',
  'Radio License',
  'Rating Certificate',
  'Insurance',
  'Safety Equipment',
  'Life Raft Certificate',
  'Keel Inspection Certificate',
  'Stability Certificate',
];

/** Polling interval for bulk status (ms). */
const BULK_POLL_INTERVAL = 2000;

/** Returns the file extension in lowercase. */
function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
}

/** Returns true when the file is a ZIP archive. */
function isZipFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  return ext === '.zip';
}

/** Validates file format. Returns error message or null. */
function validateFormat(file: File): string | null {
  const ext = getFileExtension(file.name);
  const mimeOk = Object.keys(ACCEPTED_TYPES).includes(file.type);
  const extOk = ACCEPTED_EXTENSIONS.includes(ext);
  if (!mimeOk && !extOk) {
    return `Unsupported file format "${ext || file.type}". Accepted formats: PDF, JPG, PNG, ZIP.`;
  }
  return null;
}

/** Validates file size. Returns error message or null. */
function validateSize(file: File): string | null {
  const limit = isZipFile(file) ? MAX_ZIP_SIZE : MAX_INDIVIDUAL_SIZE;
  const limitLabel = isZipFile(file) ? '50MB' : '10MB';
  if (file.size > limit) {
    return `File size exceeds ${limitLabel} limit.`;
  }
  return null;
}

const DocumentUploadPage: React.FC<DocumentUploadPageProps> = ({ onUploadSuccess }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedDoc, setUploadedDoc] = useState<Document | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [bulkStatus, setBulkStatus] = useState<BulkProcessingStatus | null>(null);
  const [classification, setClassification] = useState<DocumentClassification | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [selectedCrewId, setSelectedCrewId] = useState<string>('');
  const [showCrewSelector, setShowCrewSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Load crew members on mount. */
  useEffect(() => {
    const loadCrew = async () => {
      try {
        const crew = await crewService.getCrew();
        setCrewMembers(crew);
      } catch (err) {
        console.error('Failed to load crew members:', err);
      }
    };
    loadCrew();
  }, []);

  /** Stop polling for bulk status. */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  /** Poll bulk processing status until completed or failed. */
  const startPolling = useCallback(
    (jobId: string) => {
      stopPolling();
      pollingRef.current = setInterval(async () => {
        try {
          const status = await documentService.getBulkStatus(jobId);
          setBulkStatus(status);
          if (status.status === 'completed' || status.status === 'failed') {
            stopPolling();
          }
        } catch {
          // Silently continue polling on transient errors
        }
      }, BULK_POLL_INTERVAL);
    },
    [stopPolling]
  );

  /** Classify a file before upload. */
  const classifyFile = async (file: File) => {
    // Skip classification for ZIP files
    if (isZipFile(file)) {
      return null;
    }

    setClassifying(true);
    try {
      const result = await documentService.classifyDocument(file);
      setClassification(result);
      
      // Auto-set document type if confidence is high
      if (result.confidence >= 0.7 && result.suggestedType !== 'unknown') {
        setDocumentType(result.suggestedType);
      }
      
      // Show crew selector if it's a crew document
      if (result.isCrewDocument) {
        setShowCrewSelector(true);
        
        // Try to match holder name to a crew member
        if (result.extractedData.holderName) {
          const holderName = result.extractedData.holderName.toLowerCase();
          const matchedCrew = crewMembers.find(c => 
            `${c.firstName} ${c.lastName}`.toLowerCase().includes(holderName) ||
            holderName.includes(c.firstName.toLowerCase()) ||
            holderName.includes(c.lastName.toLowerCase())
          );
          if (matchedCrew) {
            setSelectedCrewId(matchedCrew.crewId);
          }
        }
      } else {
        setShowCrewSelector(false);
        setSelectedCrewId('');
      }
      
      return result;
    } catch (err) {
      console.error('Classification failed:', err);
      return null;
    } finally {
      setClassifying(false);
    }
  };

  /** Handle a selected file: validate, classify, and prepare for upload. */
  const handleFileSelected = async (file: File) => {
    // Reset state
    setValidationError(null);
    setUploadError(null);
    setUploadedDoc(null);
    setBulkStatus(null);
    setUploadProgress(0);
    setClassification(null);
    setSelectedFile(null);
    setShowCrewSelector(false);
    setSelectedCrewId('');

    // Validate format
    const formatErr = validateFormat(file);
    if (formatErr) {
      setValidationError(formatErr);
      return;
    }

    // Validate size
    const sizeErr = validateSize(file);
    if (sizeErr) {
      setValidationError(sizeErr);
      return;
    }

    setSelectedFile(file);

    // Classify the file (for PDFs and images)
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      await classifyFile(file);
    }
  };

  /** Upload the selected file. */
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(10);

    try {
      const metadata: { 
        documentType?: string; 
        crewId?: string;
        holderName?: string;
        issuer?: string;
        issueDate?: string;
        expiryDate?: string;
        certificateNumber?: string;
      } = {};
      
      if (documentType) {
        metadata.documentType = documentType;
      } else if (classification?.suggestedType && classification.suggestedType !== 'unknown') {
        metadata.documentType = classification.suggestedType;
      }
      if (selectedCrewId) {
        metadata.crewId = selectedCrewId;
      }
      
      // Include extracted data from classification
      if (classification?.extractedData) {
        if (classification.extractedData.holderName) {
          metadata.holderName = classification.extractedData.holderName;
        }
        if (classification.extractedData.issuer) {
          metadata.issuer = classification.extractedData.issuer;
        }
        if (classification.extractedData.issueDate) {
          metadata.issueDate = classification.extractedData.issueDate;
        }
        if (classification.extractedData.expiryDate) {
          metadata.expiryDate = classification.extractedData.expiryDate;
        }
        if (classification.extractedData.certificateNumber) {
          metadata.certificateNumber = classification.extractedData.certificateNumber;
        }
      }

      setUploadProgress(50);
      const doc = await documentService.uploadDocument(selectedFile, metadata);
      setUploadProgress(100);
      setUploadedDoc(doc);

      // If ZIP, start polling for bulk status
      if (isZipFile(selectedFile) && doc.metadata?.jobId) {
        const jobId = doc.metadata.jobId as string;
        setBulkStatus({
          jobId,
          totalFiles: 0,
          processedFiles: 0,
          successCount: 0,
          failedCount: 0,
          status: 'processing',
          errors: [],
        });
        startPolling(jobId);
      }

      // Notify parent to refresh document list
      onUploadSuccess?.();

      // Reset for next upload
      setSelectedFile(null);
      setClassification(null);
      setDocumentType('');
      setSelectedCrewId('');
      setShowCrewSelector(false);
    } catch {
      setUploadError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };



  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelected(file);
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setClassification(null);
    setDocumentType('');
    setSelectedCrewId('');
    setShowCrewSelector(false);
  };

  const bulkProgressPercent =
    bulkStatus && bulkStatus.totalFiles > 0
      ? Math.round((bulkStatus.processedFiles / bulkStatus.totalFiles) * 100)
      : 0;

  /** Get confidence level label. */
  const getConfidenceLabel = (confidence: number): string => {
    if (confidence >= 0.9) return 'Very High';
    if (confidence >= 0.7) return 'High';
    if (confidence >= 0.5) return 'Medium';
    return 'Low';
  };

  return (
    <div className="document-upload-page">
      <h1>Upload Documents</h1>

      {/* Classification result (Req 14.2, 14.3) */}
      {classification && selectedFile && (
        <div className="classification-result" data-testid="classification-result">
          <h3>Document Analysis</h3>
          <div className="classification-details">
            <div className="classification-row">
              <span className="label">Detected Type:</span>
              <span className="value">{classification.suggestedType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
            </div>
            <div className="classification-row">
              <span className="label">Confidence:</span>
              <span className={`value confidence-${getConfidenceLabel(classification.confidence).toLowerCase().replace(' ', '-')}`}>
                {getConfidenceLabel(classification.confidence)} ({(classification.confidence * 100).toFixed(0)}%)
              </span>
            </div>
            {classification.extractedData.holderName && (
              <div className="classification-row">
                <span className="label">Holder Name:</span>
                <span className="value">{classification.extractedData.holderName}</span>
              </div>
            )}
            {classification.extractedData.issuer && (
              <div className="classification-row">
                <span className="label">Issuing Authority:</span>
                <span className="value">{classification.extractedData.issuer}</span>
              </div>
            )}
            {classification.extractedData.certificateNumber && (
              <div className="classification-row">
                <span className="label">Certificate No:</span>
                <span className="value">{classification.extractedData.certificateNumber}</span>
              </div>
            )}
            {classification.extractedData.issueDate && (
              <div className="classification-row">
                <span className="label">Issue Date:</span>
                <span className="value">{classification.extractedData.issueDate}</span>
              </div>
            )}
            {classification.extractedData.expiryDate && (
              <div className="classification-row">
                <span className="label">Expiry Date:</span>
                <span className="value">{classification.extractedData.expiryDate}</span>
              </div>
            )}
            {classification.isCrewDocument && (
              <div className="classification-badge crew">
                Crew Document
              </div>
            )}
            {!classification.isCrewDocument && (
              <div className="classification-badge boat">
                Boat Document
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document type override (Req 5.5, 14.3) */}
      <div className="form-field" data-testid="document-type-section">
        <label htmlFor="documentType">Document Type {classification ? '(confirm or override)' : '(optional)'}:</label>
        <select
          id="documentType"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          data-testid="document-type-select"
        >
          <option value="">Auto-detect</option>
          {DOCUMENT_TYPES.map((dt) => (
            <option key={dt} value={dt}>
              {dt}
            </option>
          ))}
        </select>
      </div>

      {/* Crew member selection (Req 13.1) */}
      {showCrewSelector && (
        <div className="form-field" data-testid="crew-selector-section">
          <label htmlFor="crewMember">Link to Crew Member:</label>
          <select
            id="crewMember"
            value={selectedCrewId}
            onChange={(e) => setSelectedCrewId(e.target.value)}
            data-testid="crew-member-select"
          >
            <option value="">Select crew member...</option>
            {crewMembers.map((crew) => (
              <option key={crew.crewId} value={crew.crewId}>
                {crew.firstName} {crew.lastName} ({crew.asNumber})
              </option>
            ))}
          </select>
          {classification?.extractedData.holderName && selectedCrewId && (
            <p className="match-hint">
              Matched from document: {classification.extractedData.holderName}
            </p>
          )}
        </div>
      )}

      {/* Classifying indicator */}
      {classifying && (
        <div className="classifying-indicator" data-testid="classifying">
          <div className="loading-spinner small"></div>
          <span>Analyzing document...</span>
        </div>
      )}

      {/* Drop zone */}
      {!selectedFile && (
        <div
          className={`drop-zone${dragging ? ' dragging' : ''}`}
          data-testid="drop-zone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <p>
            {dragging
              ? 'Drop file here'
              : 'Drag & drop a file here, or click to select (PDF, JPG, PNG, ZIP)'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.zip"
            onChange={handleFileSelect}
            data-testid="file-input"
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Selected file info and upload button */}
      {selectedFile && !uploading && !uploadedDoc && (
        <div className="selected-file" data-testid="selected-file">
          <div className="file-info">
            <span className="file-name">{selectedFile.name}</span>
            <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
          </div>
          <div className="file-actions">
            <button 
              onClick={handleUpload} 
              className="btn-upload"
              data-testid="upload-btn"
              disabled={classifying || (showCrewSelector && !selectedCrewId && classification?.isCrewDocument)}
            >
              {classifying ? 'Analyzing...' : 'Accept & Upload'}
            </button>
            <button onClick={handleCancelUpload} className="btn-cancel" data-testid="cancel-btn" disabled={classifying}>
              Cancel
            </button>
          </div>
          {showCrewSelector && !selectedCrewId && classification?.isCrewDocument && (
            <p className="warning-message">Please select a crew member for this document.</p>
          )}
        </div>
      )}

      {/* Validation error */}
      {validationError && (
        <p className="error-message" data-testid="validation-error">
          {validationError}
        </p>
      )}

      {/* Upload progress */}
      {uploading && (
        <div data-testid="upload-progress">
          <p>Uploading… {uploadProgress}%</p>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${uploadProgress}%` }}
              data-testid="progress-bar-fill"
            />
          </div>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <p className="error-message" data-testid="upload-error">
          {uploadError}
        </p>
      )}

      {/* Upload success */}
      {uploadedDoc && (
        <div data-testid="upload-success">
          <p className="success-message">
            Document uploaded successfully (ID: {uploadedDoc.documentId}).
          </p>
        </div>
      )}

      {/* Bulk processing status for ZIP uploads (Req 13.2) */}
      {bulkStatus && (
        <div data-testid="bulk-status">
          <h2>Bulk Processing Status</h2>
          <p data-testid="bulk-status-label">
            Status: {bulkStatus.status}
          </p>
          <p data-testid="bulk-progress-text">
            Processed: {bulkStatus.processedFiles} / {bulkStatus.totalFiles}
          </p>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${bulkProgressPercent}%` }}
              data-testid="bulk-progress-bar-fill"
            />
          </div>
          {bulkStatus.status === 'completed' && (
            <div data-testid="bulk-summary">
              <p data-testid="bulk-success-count">Successful: {bulkStatus.successCount}</p>
              <p data-testid="bulk-failed-count">Failed: {bulkStatus.failedCount}</p>
              {bulkStatus.errors.length > 0 && (
                <ul data-testid="bulk-error-list">
                  {bulkStatus.errors.map((err, idx) => (
                    <li key={idx} data-testid={`bulk-error-${idx}`}>
                      {err.fileName}: {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentUploadPage;
